import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuthStore } from '@/store/auth.store';
import { useChatStore } from '@/store/chat.store';
import { chatQueryKeys } from '@/services/chat.service';
import { BASE_URL } from '@/services/api';
import { wsClient, type WsEvent } from '@/services/wsClient';

const WS_URL = import.meta.env.VITE_WS_URL;
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh if < 5 min left
// Polling REST fallback enquanto reconectando, para o inbox não ficar parado.
const RECONNECT_POLL_DELAY_MS = 3000;
const RECONNECT_POLL_INTERVAL_MS = 10000;

/**
 * Decode JWT payload to read expiration time.
 * Returns null if the token is malformed.
 */
function getTokenExpiration(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Get a valid (non-expired) ID token, refreshing if necessary.
 * Returns null if no token is available or refresh fails.
 */
async function getValidToken(): Promise<string | null> {
  const { tokens, updateTokens, logout } = useAuthStore.getState();
  if (!tokens?.idToken) return null;

  const expiresAt = getTokenExpiration(tokens.idToken);

  // If we can't decode exp or token has plenty of time left, use as-is
  if (expiresAt === null || expiresAt - Date.now() > TOKEN_REFRESH_BUFFER_MS) {
    return tokens.idToken;
  }

  // Token is near expiry or already expired — refresh
  if (!tokens.refreshToken) {
    logout();
    return null;
  }

  try {
    const response = await axios.post(`${BASE_URL}/auth/refresh`, {
      refreshToken: tokens.refreshToken,
    });
    const newTokens = response.data.data.tokens;
    updateTokens(newTokens);
    return newTokens.idToken;
  } catch {
    logout();
    window.location.href = '/login';
    return null;
  }
}

if (WS_URL) {
  wsClient.configure(WS_URL, getValidToken);
}

/**
 * Hook ponte: registra o WebSocket singleton no React. A conexão fica viva
 * pela sessão toda — connect/disconnect só dispara em login/logout.
 */
export function useWebSocket() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Conecta/desconecta conforme login. Os hooks (queryClient, navigate) não
  // entram nas deps porque o handler é registrado em outro effect que sempre
  // lê a versão atual via closure recreada a cada render.
  useEffect(() => {
    if (!isAuthenticated) {
      wsClient.disconnect();
      return;
    }
    void wsClient.connect();
    // Sem cleanup aqui: queremos manter a conexão viva entre re-renders.
    // O disconnect só acontece no logout (próximo run deste effect com
    // isAuthenticated=false).
  }, [isAuthenticated]);

  // Registra handler de eventos. Re-registra quando deps mudam, mas isso é
  // barato (Set add/delete) e não toca na conexão.
  useEffect(() => {
    if (!isAuthenticated) return;

    const handler = (wsEvent: WsEvent) => {
      switch (wsEvent.type) {
        case 'new_message':
          queryClient.invalidateQueries({ queryKey: chatQueryKeys.lists() });
          queryClient.invalidateQueries({ queryKey: chatQueryKeys.counts() });
          if (
            wsEvent.data &&
            typeof wsEvent.data === 'object' &&
            'conversationId' in wsEvent.data
          ) {
            queryClient.invalidateQueries({
              queryKey: chatQueryKeys.messages(
                (wsEvent.data as { conversationId: string }).conversationId
              ),
            });
          }
          break;

        case 'conversation_updated':
          queryClient.invalidateQueries({ queryKey: chatQueryKeys.lists() });
          queryClient.invalidateQueries({ queryKey: chatQueryKeys.counts() });
          // Invalida também o detalhe da conversa aberta — sem isso, mudanças de
          // status/atribuição/número (ex.: handoff por-número) não refrescam a
          // conversa que o operador está visualizando, só a lista.
          if (
            wsEvent.data &&
            typeof wsEvent.data === 'object' &&
            'conversationId' in wsEvent.data
          ) {
            queryClient.invalidateQueries({
              queryKey: chatQueryKeys.detail(
                (wsEvent.data as { conversationId: string }).conversationId
              ),
            });
          }
          break;

        case 'message_status_updated': {
          const statusData = wsEvent.data as
            | { conversationId: string; messageId: string; status: string }
            | undefined;
          if (statusData?.conversationId) {
            queryClient.invalidateQueries({
              queryKey: chatQueryKeys.messages(statusData.conversationId),
            });
          }
          break;
        }

        case 'message_edited': {
          // Patch otimista no cache: marca a mensagem com editedAt para
          // exibir o badge "(editada)" sem flash de refetch.
          const editData = wsEvent.data as
            | { conversationId: string; messageId: string; editedAt: string }
            | undefined;
          if (editData?.conversationId && editData.messageId) {
            queryClient.setQueryData(
              chatQueryKeys.messages(editData.conversationId),
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (old: any) => {
                if (!old?.pages?.length) return old;
                return {
                  ...old,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  pages: old.pages.map((p: any) => ({
                    ...p,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    data: p.data.map((m: any) =>
                      m.id === editData.messageId
                        ? { ...m, editedAt: editData.editedAt }
                        : m
                    ),
                  })),
                };
              }
            );
          }
          break;
        }

        case 'message_reaction_updated': {
          // Patch otimista direto na cache para evitar flicker — reactions sao
          // o tipo de evento que mais se beneficia de UI instantanea. Refetch
          // de fallback continua acontecendo nas demais invalidacoes.
          const reactionData = wsEvent.data as
            | {
                conversationId: string;
                messageId: string;
                reactions?: {
                  contact?: { emoji: string; updatedAt: string };
                  operators?: Record<string, { emoji: string; name?: string; updatedAt: string }>;
                };
              }
            | undefined;
          if (reactionData?.conversationId && reactionData.messageId) {
            queryClient.setQueryData(
              chatQueryKeys.messages(reactionData.conversationId),
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (old: any) => {
                if (!old?.pages?.length) return old;
                return {
                  ...old,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  pages: old.pages.map((p: any) => ({
                    ...p,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    data: p.data.map((m: any) =>
                      m.id === reactionData.messageId
                        ? { ...m, reactions: reactionData.reactions }
                        : m
                    ),
                  })),
                };
              }
            );
          }
          break;
        }

        case 'session_started':
        case 'session_ended': {
          const sessionData = wsEvent.data as
            | {
                conversationId?: string;
                contactName?: string;
                phone?: string;
                session?: { id?: string };
              }
            | undefined;
          if (sessionData?.conversationId) {
            queryClient.invalidateQueries({
              queryKey: chatQueryKeys.sessions(sessionData.conversationId),
            });
            queryClient.invalidateQueries({
              queryKey: chatQueryKeys.messages(sessionData.conversationId),
            });
            queryClient.invalidateQueries({
              queryKey: chatQueryKeys.detail(sessionData.conversationId),
            });
          }
          queryClient.invalidateQueries({ queryKey: chatQueryKeys.lists() });
          queryClient.invalidateQueries({ queryKey: chatQueryKeys.counts() });

          // Toast só dispara em session_started disparado por mensagem do
          // contato (carrega contactName). session_started criado pelo
          // operador via "nova conversa" não tem contactName e seria ruído.
          if (
            wsEvent.type === 'session_started' &&
            sessionData?.conversationId &&
            sessionData.contactName
          ) {
            const { selectedConversationId, setSelectedConversation } = useChatStore.getState();
            const isViewingThisConversation =
              selectedConversationId === sessionData.conversationId;
            if (!isViewingThisConversation) {
              const conversationId = sessionData.conversationId;
              const toastId = `session-started-${sessionData.session?.id ?? conversationId}`;
              toast.info(`Nova conversa de ${sessionData.contactName}`, {
                id: toastId,
                description: sessionData.phone,
                action: {
                  label: 'Abrir',
                  onClick: () => {
                    setSelectedConversation(conversationId);
                    navigate('/app/chat');
                  },
                },
              });
            }
          }
          break;
        }

        case 'connection_update':
          break;

        case 'connector_blocked': {
          const blockData = wsEvent.data as
            | { connectorName?: string; pausedCampaigns?: number }
            | undefined;
          const name = blockData?.connectorName ?? 'WhatsApp';
          const paused = blockData?.pausedCampaigns ?? 0;
          toast.error(`Connector "${name}" foi bloqueado pelo WhatsApp`, {
            id: `connector-blocked-${name}`,
            duration: Infinity,
            description:
              paused > 0
                ? `${paused} campanha(s) em andamento foram pausadas automaticamente. Reconecte o número em Conectores.`
                : 'Reconecte o número na página de Conectores.',
            action: {
              label: 'Conectores',
              onClick: () => navigate('/app/connectors'),
            },
          });
          break;
        }
      }
    };

    const unsubscribe = wsClient.subscribe(handler);
    return unsubscribe;
  }, [isAuthenticated, queryClient, navigate]);

  // Espelha o status da conexão no chat.store (usado por useConversations,
  // useMessages, etc. para alternar entre WS e polling REST).
  useEffect(() => {
    let pollDelay: ReturnType<typeof setTimeout> | null = null;
    let pollInterval: ReturnType<typeof setInterval> | null = null;

    const stopPolling = () => {
      if (pollDelay) {
        clearTimeout(pollDelay);
        pollDelay = null;
      }
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const startPolling = () => {
      if (pollDelay || pollInterval) return;
      pollDelay = setTimeout(() => {
        const refresh = () => {
          queryClient.invalidateQueries({ queryKey: chatQueryKeys.lists() });
          queryClient.invalidateQueries({ queryKey: chatQueryKeys.counts() });
        };
        refresh();
        pollInterval = setInterval(refresh, RECONNECT_POLL_INTERVAL_MS);
      }, RECONNECT_POLL_DELAY_MS);
    };

    const unsubscribe = wsClient.onStatusChange((status) => {
      const { setWsConnected, setWsReconnecting } = useChatStore.getState();
      if (status === 'open') {
        setWsConnected(true);
        setWsReconnecting(false);
        stopPolling();
      } else if (status === 'reconnecting' || status === 'connecting') {
        setWsConnected(false);
        setWsReconnecting(true);
        startPolling();
      } else {
        setWsConnected(false);
        setWsReconnecting(false);
        stopPolling();
      }
    });

    return () => {
      unsubscribe();
      stopPolling();
    };
  }, [queryClient]);
}
